import { ReactElement, useEffect, useRef } from 'react';
import { Stack, Typography, CircularProgress, Paper, useTheme } from '@mui/material';
import { CheckCircleRounded } from '@mui/icons-material';
import { t } from 'i18next';
import { ActionResponseStatusAi } from '@/ai/enum/action-response-status-ai';

export const StepInfo = ({
    status,
    currentStep,
    totalSteps,
    titleComp,
    finishedComp,
    processingComp,
}: {
    status: string;
    currentStep: number;
    totalSteps: number;
    titleComp: string;
    finishedComp: ReactElement;
    processingComp: ReactElement;
}) => {
    const theme = useTheme();
    const isLastStep = currentStep === totalSteps;
    const isComplete = status === ActionResponseStatusAi.COMPLETE;
    const iconColor = isComplete && isLastStep ? theme.palette.success.main : theme.palette.primary.main;
    const textColor = iconColor;
    const stepRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (status === ActionResponseStatusAi.COMPLETE) {
            stepRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [status]);

    return (
        <>
            <Paper
                elevation={0}
                sx={{
                    background: 'background.paper-elevation-24',
                    borderRadius: 2,
                    p: 2,
                    border: `1px solid ${iconColor}`,
                    ...(!isComplete && {
                        position: 'fixed',
                        bottom: 5,
                        width: '500px',
                        zIndex: 1,
                        maxWidth: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                    }),
                }}
                ref={stepRef}
            >
                <Stack direction="column" spacing={2} alignItems="left">
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
                        <Stack>
                            {isComplete ? (
                                <CheckCircleRounded sx={{ color: iconColor, fontSize: 28 }} />
                            ) : (
                                <CircularProgress size="25px" color="primary" />
                            )}
                        </Stack>
                        <Stack>
                            <Typography variant="subtitle1" fontWeight={600} color={textColor}>
                                {t('onboarding.chat.step-info-title', { currentStep, totalSteps, titleComp })}
                            </Typography>
                        </Stack>
                    </Stack>
                    <Stack pb={0.5}>
                        <Typography variant="body2" color={textColor}>
                            {status !== 'complete' ? processingComp : finishedComp}
                        </Typography>
                    </Stack>
                </Stack>
            </Paper>
        </>
    );
};
